// components/Chat/ZenoResourceCard.jsx
//
// Visual card for a resource spec Zeno is building — avatar (now) or agent /
// workflow (ready for when those quick-create flows are enabled). Rendered in
// the chat in place of a ```zeno:card {json}``` block (see zenoCards.js).
import React, { useState } from 'react';
import {
  Sparkles,
  Headset,
  Workflow as WorkflowIcon,
  UserSquare,
  Check,
  Loader2,
  Shield,
  Cpu,
  Globe,
  Wrench,
  ChevronDown,
  ExternalLink,
} from 'lucide-react';

// Build the deep-link to a specific resource from its id. The agent only sends
// the id (never a URL); the front-end owns the routing. Avatars are wired now;
// agents/workflows follow once their deep-link routes land.
function resourceHref(card) {
  if (!card?.id) return null;
  if (card.type === 'avatar') {
    return `/dashboard?view=Avatars&templateId=${encodeURIComponent(card.id)}`;
  }
  if (card.type === 'agent') {
    // Only the agent id is needed — the dashboard resolves its project on load.
    return `/dashboard?view=Projects&agentId=${encodeURIComponent(card.id)}`;
  }
  return null;
}

const STATUS = {
  draft: { label: 'Draft', cls: 'border-white/15 bg-white/5 text-gray-300' },
  creating: { label: 'Creating…', cls: 'border-[#8844F5]/40 bg-[#8844F5]/15 text-[#c9a9ff]' },
  created: { label: 'Created', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' },
};

function StatusBadge({ status }) {
  const s = STATUS[status];
  if (!s) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>
      {status === 'creating' ? <Loader2 className="h-3 w-3 animate-spin" /> : status === 'created' ? <Check className="h-3 w-3" /> : null}
      {s.label}
    </span>
  );
}

function Chip({ icon: Icon, children }) {
  if (!children) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs text-gray-300">
      {Icon && <Icon className="h-3 w-3 text-[#b98bff]" />}
      {children}
    </span>
  );
}

const TYPE_META = {
  avatar: { icon: UserSquare, kind: 'Avatar' },
  agent: { icon: Headset, kind: 'Agent' },
  workflow: { icon: WorkflowIcon, kind: 'Workflow' },
};

export default function ZenoResourceCard({ card, className = '' }) {
  const [expanded, setExpanded] = useState(false);
  if (!card || !card.type) return null;
  const meta = TYPE_META[card.type] || TYPE_META.avatar;
  const TypeIcon = meta.icon;
  const sections = Array.isArray(card.sections) ? card.sections : [];
  const steps = Array.isArray(card.steps) ? card.steps : [];
  const detailCount = sections.length + steps.length;
  const href = resourceHref(card);

  const thumb = card.image_url ? (
    <img src={card.image_url} alt={card.name || meta.kind} className="h-full w-full object-cover" />
  ) : (
    <TypeIcon className="h-5 w-5 text-[#b98bff]" />
  );

  return (
    <div
      className={`w-full max-w-[440px] overflow-hidden rounded-2xl border border-[#8844F5]/25 bg-[#1b1b1f] shadow-[0_2px_12px_rgba(0,0,0,0.3)] ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${meta.kind} in a new tab`}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#14121a] ring-1 ring-transparent transition hover:ring-[#8844F5]/50"
          >
            {thumb}
          </a>
        ) : (
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#14121a]">
            {thumb}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-[#b98bff]">
              <Sparkles className="h-3 w-3" /> {meta.kind}
            </span>
            <StatusBadge status={card.status} />
          </div>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${meta.kind} in a new tab`}
              className="group inline-flex max-w-full items-center gap-1 text-sm font-semibold text-white transition-colors hover:text-[#b98bff]"
            >
              <span className="truncate">{card.name || `New ${meta.kind.toLowerCase()}`}</span>
              <ExternalLink className="h-3 w-3 flex-shrink-0 text-gray-500 transition-colors group-hover:text-[#b98bff]" />
            </a>
          ) : (
            <div className="truncate text-sm font-semibold text-white">{card.name || `New ${meta.kind.toLowerCase()}`}</div>
          )}
          {(card.purpose || card.business || card.description) && (
            <div className="truncate text-xs text-gray-400">
              {[card.purpose, card.business].filter(Boolean).join(' · ') || card.description}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="space-y-3 px-4 py-3">
        {/* Chips */}
        <div className="flex flex-wrap gap-1.5">
          <Chip icon={Wrench}>{card.tone}</Chip>
          <Chip icon={Globe}>{card.language}</Chip>
          {card.output_mode && <Chip>{card.output_mode === 'structured_json' ? 'Structured JSON' : 'Normal'}</Chip>}
          <Chip icon={Cpu}>{card.model}</Chip>
          {card.execution_mode && <Chip>{card.execution_mode}</Chip>}
          {Array.isArray(card.skills) && card.skills.length > 0 && (
            <Chip icon={Wrench}>{card.skills.length} skill{card.skills.length > 1 ? 's' : ''}</Chip>
          )}
          {card.trigger && <Chip>{card.trigger}</Chip>}
          {typeof card.is_public === 'boolean' && (
            <Chip>{card.is_public ? 'Public' : 'Private'}</Chip>
          )}
          {card.project && <Chip>{card.project}</Chip>}
        </div>

        {/* Agent/workflow description (avatar shows it in the header subtitle) */}
        {card.type !== 'avatar' && card.description && (
          <p className="text-xs leading-relaxed text-gray-300">{card.description}</p>
        )}

        {/* Details — collapsed by default (just names); expand for full content */}
        {detailCount > 0 && (
          <div className="space-y-2">
            {expanded ? (
              <>
                {sections.map((s, i) => (
                  <div key={s.name || i} className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
                    <div className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-[#b98bff]">
                      <Shield className="h-3 w-3" /> {s.name}
                    </div>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-gray-300">{s.content}</p>
                  </div>
                ))}
                {steps.length > 0 && (
                  <ol className="space-y-1.5">
                    {steps.map((st, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                        <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-[#8844F5]/20 text-[10px] font-semibold text-[#b98bff]">
                          {i + 1}
                        </span>
                        <span>
                          <span className="font-medium text-gray-200">{st.name}</span>
                          {st.detail ? ` — ${st.detail}` : ''}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </>
            ) : (
              <div className="flex flex-wrap gap-1">
                {sections.map((s, i) => (
                  <span
                    key={s.name || i}
                    className="rounded-md bg-white/[0.05] px-2 py-0.5 text-[11px] text-gray-400"
                  >
                    {s.name}
                  </span>
                ))}
                {steps.map((st, i) => (
                  <span
                    key={i}
                    className="rounded-md bg-white/[0.05] px-2 py-0.5 text-[11px] text-gray-400"
                  >
                    {i + 1}. {st.name}
                  </span>
                ))}
              </div>
            )}
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-[#b98bff] transition-colors hover:text-white"
            >
              {expanded ? 'Hide details' : `Show details (${detailCount})`}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
